// =============================================================================
// src/chrome/copilot-sidebar/copilot-sidebar-markup.ts
// Markup estatico da sidebar Notion-style do Copiloto IA.
//
// Proveniencia: porte FIEL de unified.html linhas 125-263 — <aside class="copilot">
//   (contexto atual + atenção/por quê + oportunidades táticas) seguido do botao
//   .copilot-reopen. Mesmas tags, classes, ids, atributos aria, textos e icones
//   Tabler. Os 3 cards táticos prioritários sao estaticos (CopilotTactics
//   so injeta os "Específicos desta vista" em #copilotTacticsSpecific).
//
// Conteudo inicial do painel "Contexto atual" mantido verbatim do markup; o
//   bootstrap do insight-panel-controller reescreve estes valores no init().
// =============================================================================

/**
 * Devolve o HTML literal da sidebar do Copiloto + botao de reabrir.
 *
 * No shell index.html este markup substitui o conteudo de #aiCopilot (sidebar) e
 *   #copilotReopenMount (botao). A integracao (main.ts) decide o ponto de
 *   montagem; aqui devolvemos a string completa para paridade de marcacao.
 */
export function buildCopilotSidebarMarkup(): string {
  return SIDEBAR_ASIDE_MARKUP + REOPEN_BUTTON_MARKUP;
}

/** <aside class="copilot"> completo (unified.html 125-258). */
const SIDEBAR_ASIDE_MARKUP = `<aside class="copilot" id="aiCopilot" aria-label="Copiloto de insights">
  <header class="copilot-head">
    <div class="copilot-brand">
      <span class="ai-orb ai-orb--xs" aria-hidden="true"><svg class="ai-orb__svg" viewBox="0 0 32 32"><use href="#aiOrbSym"/></svg></span>
      <span class="copilot-brand-name">Copiloto IA</span>
      <span class="copilot-brand-sub">Manaus</span>
    </div>
    <button class="copilot-collapse" id="copilotCollapse" type="button" aria-label="Recolher copiloto">
      <i class="ti ti-layout-sidebar-left-collapse"></i>
    </button>
  </header>

  <div class="copilot-scroll">

    <!-- ── Contexto atual ─────────────────────────────────── -->
    <section class="copilot-group">
      <div class="copilot-group-label">Contexto atual</div>
      <div class="copilot-context" id="copilotPanel" aria-live="polite">
        <div class="copilot-section-tag" id="copilotSectionLabel">01 · Mapa Eleitoral</div>
        <div class="copilot-context-body" id="copilotBody">
          <div class="copilot-dado-row">
            <span class="copilot-dado-label">DADO</span>
            <span class="copilot-dado" id="copilotDado">+1.240 votos</span>
          </div>
          <h3 class="copilot-headline" id="copilotHeadline">Zona Oeste virou de azul pra vermelho</h3>
          <p class="copilot-detail" id="copilotDetail">Tendência crescente desde 2022. <strong>+1.240 votos</strong> viáveis em Centro-Sul se reagir até abril.</p>
          <div class="copilot-confidence">
            <span class="copilot-conf-track"><span class="copilot-conf-fill" id="copilotConfBar" style="width:78%"></span></span>
            <span class="copilot-conf-label" id="copilotConfLabel">confiança 78%</span>
          </div>
          <button class="copilot-action" id="copilotActionPrimary" type="button">
            <span class="copilot-action-text">ver estratégia</span>
            <i class="ti ti-arrow-right"></i>
          </button>
        </div>
      </div>
    </section>

    <!-- ── Atenção / Por quê (collapsible) ─────────────────── -->
    <section class="copilot-group">
      <details class="copilot-attention" id="copilotAtencao">
        <summary class="copilot-attention-summary">
          <span class="copilot-attention-chip" id="copilotAttentionChip">atenção</span>
          <span class="copilot-attention-title">Por quê &amp; ressalvas</span>
          <i class="ti ti-chevron-down copilot-chevron"></i>
        </summary>
        <p class="copilot-why" id="copilotWhy">Cruzei série histórica 2014–2024 da Zona Oeste com dados socioeconômicos.</p>
      </details>
    </section>

    <!-- ── Oportunidades táticas ───────────────────────────── -->
    <section class="copilot-group copilot-tactics">
      <div class="copilot-group-label">
        <span>Oportunidades táticas</span>
        <span class="copilot-group-counter" id="copilotTacticsCount">3</span>
      </div>

      <div class="copilot-tactics-sub">
        <div class="copilot-tactics-sub-label">
          <i class="ti ti-flame"></i>
          <span>Prioritárias da campanha</span>
        </div>

        <div id="copilotTacticsPriority" class="copilot-tactics-priority">
        <article class="copilot-tactic-card" data-priority="alta">
          <div class="copilot-tactic-head">
            <span class="copilot-tactic-priority copilot-tactic-priority--alta">Alta</span>
            <span class="copilot-tactic-tag">Abstenção</span>
            <span class="copilot-tactic-confidence">82%</span>
          </div>
          <p class="copilot-tactic-desc">Aumentar presença em bairros com abstenção acima de 18%</p>
          <div class="copilot-tactic-impact">
            <i class="ti ti-trending-up"></i>
            <span>+3.200 votos viáveis</span>
          </div>
          <button class="copilot-tactic-cta" type="button">
            <span>Ativar tática</span>
            <i class="ti ti-arrow-right"></i>
          </button>
        </article>

        <article class="copilot-tactic-card" data-priority="media">
          <div class="copilot-tactic-head">
            <span class="copilot-tactic-priority copilot-tactic-priority--media">Média</span>
            <span class="copilot-tactic-tag">Engajamento</span>
            <span class="copilot-tactic-confidence">71%</span>
          </div>
          <p class="copilot-tactic-desc">Engajar eleitores jovens (18-24) com baixo pertencimento na zona norte</p>
          <div class="copilot-tactic-impact">
            <i class="ti ti-trending-up"></i>
            <span>+1.800 votos potenciais</span>
          </div>
          <button class="copilot-tactic-cta" type="button">
            <span>Ativar tática</span>
            <i class="ti ti-arrow-right"></i>
          </button>
        </article>

        <article class="copilot-tactic-card" data-priority="media">
          <div class="copilot-tactic-head">
            <span class="copilot-tactic-priority copilot-tactic-priority--media">Média</span>
            <span class="copilot-tactic-tag">Coligação</span>
            <span class="copilot-tactic-confidence">68%</span>
          </div>
          <p class="copilot-tactic-desc">Parceria com lideranças religiosas locais para ampliar pertencimento comunitário</p>
          <div class="copilot-tactic-impact">
            <i class="ti ti-broadcast"></i>
            <span>Alcance +14%</span>
          </div>
          <button class="copilot-tactic-cta copilot-tactic-cta--ghost" type="button">
            <span>Ver detalhes</span>
            <i class="ti ti-arrow-right"></i>
          </button>
        </article>
        </div>
      </div>

      <div class="copilot-tactics-sub" id="copilotTacticsSpecificWrap">
        <div class="copilot-tactics-sub-label">
          <i class="ti ti-target-arrow"></i>
          <span>Específicas desta vista</span>
        </div>
        <div id="copilotTacticsSpecific" class="copilot-tactics-specific"></div>
      </div>
    </section>

  </div>

  <footer class="copilot-foot">
    <!-- Copiloto IA DESATIVADO (2026-07-21): a sidebar continua visivel, mas a acao
         primaria fica inerte. O atalho Ctrl+J tambem nao e mais ligado (main.ts nao
         chama CopilotSidebarChrome.init), por isso o badge do atalho saiu do rotulo:
         anunciar uma tecla que nao faz nada e pior do que nao anunciar. -->
    <button class="copilot-new-chat" id="copilotNewChat" type="button"
            disabled aria-disabled="true" title="Copiloto IA indisponível">
      <i class="ti ti-message-plus"></i>
      <span>Nova pergunta</span>
    </button>
  </footer>
</aside>`;

/** Botao que reabre a sidebar quando recolhida (unified.html 260-263). */
const REOPEN_BUTTON_MARKUP = `

<!-- Trigger button that reopens the sidebar when collapsed -->
<button class="copilot-reopen" id="copilotReopen" type="button" aria-label="Abrir copiloto">
  <i class="ti ti-layout-sidebar-left-expand"></i>
</button>`;
